import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button as ButtonBase,
  Typography as TypographyBase,
} from "@mui/material";

import {
  campaignErrorSelector as isErrorCampaignsSelector,
  isJoinCampTnCCheckedSelector,
  campaignIsLoadingSelector as isLoadingCampaignsSelector,
  isSavedSelector,
} from "@/features/campaigns/shared/store/campaignSlice";
import {
  joinCampaignThunk,
  resetIsSavedDraftSuccess,
} from "@/features/campaigns/shared/store/campaignSlice";
import { LoaderContext, GlobalContext } from "@/helpers/contexts/contexts";
import NoDataFoundBase from "@/components/NoDataFound";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import ParticipantPageLayoutBase from "@/components/layouts/page/ParticipantPageLayout";
import ConfirmJoinCampaignCardBase from "@/features/campaigns/components/joinCampaigns/ConfirmationJoinCampaignCard";
import ConfirmJoinCampaignTotalCardBase from "@/features/campaigns/components/joinCampaigns/ConfirmJoinCampaignTotalCard";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import { canJoinCampaign } from "@/helpers/utils";
import PageLayoutBase from "@/components/layouts/page/PageLayout";
import ConfirmStripeEmbedCheckoutPopupBase from "@/components/stripe/embedCheckout/ConfirmStripeEmbedCheckoutPopup";
import { stripeMicroService } from "@/shared/services/stripeMicroService";
import { logger } from "@/helpers/loggerHelper";
import { RpSweetAlert } from "@/components/alerts/RpSweetAlert";
import EditLocationModalBase from "./location/EditLocationModal";
import FullScreenLoaderBase from "@/components/loaders/FullScreenLoader";
import { HTTP_STATUS } from "@/config/constants";
import TermsAndConditionsBase from "@/components/tnc/TermsAndConditions";
import { getCreativePackageLabel } from "../helpers/creativeLabel";
import {
  buildChannelSelectionData,
  buildSelectedPackageCreativeData,
  getDefaultSelectedChannelIds,
  isParticipantChannelSelectionEnabled,
} from "@/features/campaigns/shared/helpers/channelSelectionHelper";
import { ROUTES } from "@/helpers/routes/routePaths";
import { grantThankYouAccessForFlow } from "@/shared/helpers/thankYouAccessHelper";

type ContextId = string | number;

type LoggedInUser = {
  id?: ContextId;
  email?: string;
  first_name?: string;
  last_name?: string;
  participantData?: {
    id?: ContextId;
  };
};

type SelectedBrand = {
  id?: ContextId;
  brand_name?: string;
  system_generated_brand_id?: string;
  location_fees_covered?: boolean;
  is_white_label?: boolean;
};

type ConfirmationLocation = {
  locationId?: ContextId;
  amount?: unknown;
  start_date?: unknown;
  end_date?: unknown;
  creatives?: ContextId[];
  channels?: ContextId[];
  creativeData?: Array<Record<string, unknown>>;
  locationName?: string;
  brand_contribution_amount?: unknown;
  system_generated_location_id?: string;
  [key: string]: unknown;
};

type CampaignLocationDetail = {
  id?: ContextId;
  creatives?: Array<Record<string, unknown>>;
  location_fees_covered?: boolean;
  [key: string]: unknown;
};

type ParticipantCampaign = {
  fk_campaign_id?: ContextId;
  fk_campaign_name?: string;
  start_date?: unknown;
  end_date?: unknown;
  total_budget?: unknown;
  splitBudgetEqually?: boolean;
  campaign_type?: string;
  duration?: unknown;
  locationData: ConfirmationLocation[];
};

type CampaignData = {
  id?: ContextId;
  can_participant_select_specific_channels?: unknown;
  campaign_name?: string;
  campaign_type?: string;
  duration?: unknown;
  start_date?: unknown;
  end_date?: unknown;
  budget_type?: string;
  local_selection?: boolean;
  enable_payment_through_portal?: boolean;
  is_brand_paid?: boolean;
  participant_campaign?: ParticipantCampaign;
  locations?: {
    subscribed?: CampaignLocationDetail[];
    brandPaid?: CampaignLocationDetail[];
  };
  [key: string]: unknown;
};

type CampaignResponse = {
  status?: number;
  data?: {
    rows?: CampaignData[];
  };
};

const PageLayout = PageLayoutBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const ParticipantPageLayout =
  ParticipantPageLayoutBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const NoDataFound = NoDataFoundBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const ConfirmJoinCampaignCard =
  ConfirmJoinCampaignCardBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const ConfirmJoinCampaignTotalCard =
  ConfirmJoinCampaignTotalCardBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const ConfirmStripeEmbedCheckoutPopup =
  ConfirmStripeEmbedCheckoutPopupBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const EditLocationModal =
  EditLocationModalBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const TermsAndConditions =
  TermsAndConditionsBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const FullScreenLoader = FullScreenLoaderBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const Button = ButtonBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const Typography = TypographyBase as unknown as React.ComponentType<
  Record<string, unknown>
>;

const JoinCampaignConfirmationPage = () => {
  const loader = useContext(LoaderContext) as (_isLoading: boolean) => void;
  const { selectedBrand, loggedInUser } = useContext(GlobalContext) as {
    selectedBrand?: SelectedBrand;
    loggedInUser?: LoggedInUser;
  };
  const { state } = useLocation() as { state?: { campaignId?: ContextId } };
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isSaved = useSelector(isSavedSelector);
  const isError = useSelector(isErrorCampaignsSelector);
  const isLoadingCampaigns = useSelector(isLoadingCampaignsSelector);

  const [openPayment, setOpenPayment] = useState(false);

  const [campaign, setCampaign] = useState<CampaignData | null>(null);
  const [loading, setLoading] = useState(false);
  const [editLocation, setEditLocation] = useState<ConfirmationLocation | null>(
    null
  );
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);

  const isJoinCampTnCChecked = useSelector(isJoinCampTnCCheckedSelector);
  const [isTnCChecked, setIsTnCChecked] = useState(
    selectedBrand?.location_fees_covered ? isJoinCampTnCChecked : true
  );

  const getCampaign = useCallback(
    async (isUpdatingLocation = false) => {
      if (!isUpdatingLocation) {
        setLoading(true);
      }
      try {
        const campaignReqData = {
          id: state.campaignId,
          brand_id: selectedBrand?.id,
          fk_participant_id: loggedInUser?.participantData?.id,
        };
        const res = (await campaignService.getSingleCampaign(
          campaignReqData
        )) as CampaignResponse;
        if (res.status === HTTP_STATUS.OK) {
          let campaignData = res.data?.rows?.[0];

          if (campaignData.is_brand_paid) {
            campaignData.locations.subscribed = campaignData.locations.brandPaid
              ?.length
              ? campaignData.locations.brandPaid
              : campaignData.locations.subscribed;
          }

          // reset campaign when not found
          if (!campaignData) {
            setCampaign(null);
            return;
          }

          setCampaign(campaignData);
        }
      } catch (err) {
        logger.error("Error in getCampaign", { err });
      } finally {
        setLoading(false);
        setIsUpdatingLocation(false);
      }
    },
    [state?.campaignId, selectedBrand?.id, loggedInUser?.participantData?.id]
  );

  useEffect(() => {
    if (state?.campaignId) {
      getCampaign();
    } else {
      setLoading(false);
    }
  }, [state, getCampaign]);

  useEffect(() => {
    loader(isLoadingCampaigns);
  }, [isLoadingCampaigns]);

  useEffect(() => {
    loader(loading);
  }, [loading, loader]);

  useEffect(() => {
    if (isSaved) {
      (dispatch as any)(resetIsSavedDraftSuccess());
      // redirect to dashboard if saved
      grantThankYouAccessForFlow({
        stateFrom: "join_campaign_confirm",
      });
      navigate(ROUTES.THANK_YOU, {
        state: {
          from: "join_campaign_confirm",
          campaignType: campaign?.campaign_type,
        },
        replace: true,
      });
    }
  }, [isSaved]);

  useEffect(() => {
    if (isError?.response?.data) {
      const redirect = isError?.response?.data?.redirect;
      if (redirect) {
        navigate(`/launch-campaign`, {
          replace: true,
        });
      }
    }
  }, [isError, navigate]);

  const totals = useMemo(() => {
    if (campaign) {
      return joinCampaignHelper.getTotalCalculations(campaign);
    }
    return {};
  }, [campaign]);

  const { participant_campaign } = campaign || {};

  const dataToSave = useCallback(
    (payment_status = 0) => {
      const { participant_campaign } = campaign || {};
      const confirmData: Record<string, unknown> = {
        brand_id: selectedBrand?.id,
        email_id: loggedInUser?.email,
        fk_campaign_id: participant_campaign.fk_campaign_id,
        // system_generated_campaign_id:
        //   participant_campaign.system_generated_campaign_id,
        fk_campaign_name: participant_campaign.fk_campaign_name,
        fk_participant_id: loggedInUser?.participantData?.id,
        is_draft: false,
        start_date: participant_campaign.start_date,
        end_date: participant_campaign.end_date,
        locationData: JSON.stringify(participant_campaign.locationData),
        user_id: loggedInUser?.id,
        user_name: `${loggedInUser?.first_name} ${loggedInUser?.last_name}`,
        payment_status: payment_status,
        total_budget: participant_campaign.total_budget,
        splitBudgetEqually: participant_campaign.splitBudgetEqually,
        campaign_type: participant_campaign.campaign_type,
        duration: participant_campaign.duration,
        // creditCardProcessingFee: totals?.creditCardProcessingFee || 0,
        budget_type: campaign?.budget_type,
        is_join_camp_tnc_checked: isTnCChecked,
        is_white_label: selectedBrand?.is_white_label,
      };

      if (campaign.budget_type === "Pre-set" && campaign.local_selection) {
        // Auto enrolled and now only creatives are selecting
        confirmData.is_draft = false;
        confirmData.is_enrolled = false;
        confirmData.is_creative_selected = 0;
      }

      return confirmData;
    },
    [campaign, totals, loggedInUser, isTnCChecked]
  );

  const handleConfirm = (payment_status = 0) => {
    const confirmData = dataToSave(payment_status);
    (dispatch as any)((joinCampaignThunk as any)(confirmData));
  };

  // const updateLocation = useCallback(
  //   async (location, isDelete = false) => {
  //     const currentSavedData = campaign?.participant_campaign;
  //     if (!currentSavedData) {
  //       return;
  //     }

  //     try {
  //       setIsUpdatingLocation(true);
  //       const { locationData } = currentSavedData;

  //       const updatedLocationData = isDelete
  //         ? locationData.filter(
  //             (l) => String(l.locationId) !== String(location)
  //           )
  //         : locationData.map((l) => {
  //             if (String(l.locationId) === String(location?.locationId)) {
  //               return location;
  //             }
  //             return l;
  //           });

  //       const updatedData = {
  //         ...currentSavedData,
  //         email_id: loggedInUser.email,
  //         user_id: loggedInUser.id,
  //         user_name: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
  //         fk_participant_id: loggedInUser?.participantData?.id,
  //         fk_campaign_id: campaign.id,
  //         // system_generated_campaign_id: campaign.system_generated_campaign_id,
  //         fk_campaign_name: campaign.campaign_name,
  //         campaign_type: campaign.campaign_type,
  //         duration: campaign.duration,
  //         start_date: campaign.start_date,
  //         end_date: campaign.end_date,
  //         locationData: JSON.stringify(updatedLocationData),
  //         payment_status: 0,
  //         total_budget: joinCampaignHelper.getTotal(updatedLocationData),
  //         is_draft: true,
  //         splitBudgetEqually: false,
  //         budget_type: campaign?.budget_type,
  //       };

  //       const res = await campaignService.joinCampaign(updatedData);
  //       if (res.status === 201 || res.status === 200) {
  //         getCampaign(true);
  //         setEditLocation(null);
  //       }
  //     } catch (err) {
  //       logger.error("Error in handleOnDeleteLocation", { err });
  //       setIsUpdatingLocation(false);
  //     }
  //   },
  //   [campaign, loggedInUser]
  // );

  const updateLocation = useCallback(
    async (location: ConfirmationLocation, _isDelete = false) => {
      const currentSavedData = campaign?.participant_campaign;
      if (!currentSavedData) {
        return;
      }

      try {
        setIsUpdatingLocation(true);

        const locationDetail = campaign?.locations?.subscribed.find(
          (l) => String(l.id) === String(location?.locationId)
        );

        if (!locationDetail) {
          logger.error("Location detail not found", {
            location,
            locationDetail,
          });
          return;
        }

        const channelSelectionEnabled =
          isParticipantChannelSelectionEnabled(campaign);
        const selectedChannelIds = location?.channels?.length
          ? location?.channels
          : getDefaultSelectedChannelIds(
              locationDetail?.creatives,
              location?.creatives
            );
        const channelSelectionData = channelSelectionEnabled
          ? buildChannelSelectionData({
              creatives: locationDetail?.creatives as any,
              selectedPackageIds: location?.creatives,
              selectedChannelIds,
            })
          : {};
        const creativeData = channelSelectionEnabled
          ? buildSelectedPackageCreativeData({
              creatives: locationDetail?.creatives,
              selectedPackageIds: location?.creatives,
              selectedChannelIds,
            })
          : location?.creatives
              .map((cid) => {
                const creative = locationDetail?.creatives.find(
                  (cr) => cr.creative_id === cid
                );

                if (!creative) return undefined;
                return {
                  creative_id: cid,
                  creative_name: creative.creative_name,
                  sys_creative_id: creative?.sys_creative_id || "",
                };
              })
              .filter((d) => d);

        const updatedData = {
          fk_campaign_id: campaign.id,
          fk_participant_id: loggedInUser?.participantData?.id,
          fk_location_id: location?.locationId,
          start_date: location?.start_date,
          end_date: location?.end_date,
          total_budget: location?.amount,
          locationData: [
            {
              amount: location?.amount,
              budget: location?.amount,
              end_date: location?.end_date,
              creatives: location?.creatives,
              locationId: location?.locationId,
              start_date: location?.start_date,
              creativeData: creativeData,
              ...channelSelectionData,
              locationName: location?.locationName,
              brand_contribution_amount: location?.brand_contribution_amount,
              system_generated_location_id:
                location?.system_generated_location_id,
              location_fees_covered: locationDetail?.location_fees_covered,
            },
          ],
        };

        const res =
          await campaignService.updateJoinCampaignLocation(updatedData);
        if (res.status === 201 || res.status === 200) {
          getCampaign(true);
          setEditLocation(null);
        }
      } catch (err) {
        logger.error("Error in handleOnDeleteLocation", { err });
        setIsUpdatingLocation(false);
      }
    },
    [campaign, loggedInUser]
  );

  const deleteLocation = useCallback(
    async (locationId: ContextId) => {
      try {
        setIsUpdatingLocation(true);
        const res = await campaignService.deleteJoinCampaignLocation({
          fk_campaign_id: campaign.id,
          fk_participant_id: loggedInUser?.participantData?.id,
          fk_location_id: Number(locationId),
        });
        if (res.status === 200) {
          getCampaign(true);
          setEditLocation(null);
        }
      } catch (err) {
        logger.error("Error in deleteLocation", { err });
        setIsUpdatingLocation(false);
      }
    },
    [campaign, loggedInUser]
  );

  const handleOnDeleteLocation = useCallback(
    (locationId: ContextId) => () => {
      const currentSavedData = campaign?.participant_campaign;
      if (!currentSavedData) {
        return;
      }

      RpSweetAlert.fire({
        text: "Are you sure you want to remove the selections for this location?",
        imageUrl: "/img/icons/info.svg",
        imageWidth: 80,
        imageHeight: 80,
        imageAlt: "Alert",
        showCancelButton: true,
        confirmButtonText: "Remove",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await deleteLocation(locationId);
        }
      });
    },
    [updateLocation, campaign]
  );

  const handleOnClickEditSelections = useCallback(
    (location: ConfirmationLocation) => () => {
      setEditLocation(location);
    },
    []
  );

  const getConfirmationLocation = useCallback(
    (location: ConfirmationLocation) => {
      const locationDetail = campaign?.locations?.subscribed?.find(
        (l) => String(l.id) === String(location?.locationId)
      );

      if (!locationDetail?.creatives?.length) {
        return location;
      }

      return {
        ...location,
        creativeData: location?.creativeData?.map((creativeData) => {
          const creative = locationDetail.creatives.find(
            (cr) =>
              String((cr as any).creative_id) ===
              String((creativeData as any).creative_id)
          );

          if (!creative) {
            return creativeData;
          }

          return {
            ...creativeData,
            creative_name: getCreativePackageLabel(creative as any),
          };
        }),
      };
    },
    [campaign]
  );

  const handleOnUpdateLocation = useCallback(
    async (location: ConfirmationLocation, isDelete = false) => {
      await updateLocation(location, isDelete);
    },
    [updateLocation]
  );

  if (loading) {
    return (
      <PageLayout>
        <NoDataFound message="Loading..." />
      </PageLayout>
    );
  }

  let redirect = false;

  if (!state || !state.campaignId) {
    redirect = true;
  } else if (campaign && !campaign?.id) {
    redirect = true;
  }

  if (redirect) {
    navigate(`/dashboard`, { replace: true });
  } else if (!canJoinCampaign(campaign)) {
    return (
      <PageLayout>
        <ParticipantPageLayout className="pt-5 confirm-page">
          <NoDataFound
            message={
              <div className="d-flex-col-center">
                <div>
                  You have already joined this campaign or it is
                  in-active/completed/not customizable.
                </div>
                <Button
                  component={Link}
                  to={`/dashboard`}
                  variant="contained"
                  color="primary"
                  className="my-4"
                  size="large"
                >
                  Back to dashboard
                </Button>
              </div>
            }
          />
        </ParticipantPageLayout>
      </PageLayout>
    );
  }

  if (!loading && !campaign) {
    return <NoDataFound />;
  }

  const renderLocationsList = () => {
    if (
      !participant_campaign ||
      participant_campaign?.locationData.length === 0
    )
      return null;

    return (
      <div className="row mt-3">
        {participant_campaign?.locationData?.map((loc) => {
          const confirmationLocation = getConfirmationLocation(loc);

          return (
            <div className="col-12 mb-2" key={loc?.locationId}>
              <ConfirmJoinCampaignCard
                location={confirmationLocation}
                campaign={campaign}
                onDeleteLocation={handleOnDeleteLocation}
                onEditSelections={handleOnClickEditSelections}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderTotals = () => {
    return (
      <ConfirmJoinCampaignTotalCard
        totals={totals}
        enable_payment_through_portal={campaign?.enable_payment_through_portal}
      />
    );
  };

  const renderNoLocation = () => {
    return (
      <div className="d-flex-col-center my-5">
        <Typography variant="p" className="fw-b mt-2 text-center">
          You haven't made selections for any location.
        </Typography>
        <Button
          variant="contained"
          component={Link}
          to={`/participant/join-campaign/${state?.campaignId}`}
          className="my-4 text-white"
          style={{ backgroundColor: "var(--status-warning)" }}
          disableElevation
        >
          Go back to campaign selections
        </Button>
      </div>
    );
  };

  return (
    <PageLayout className="confirmation">
      <div className="join-campaign-confirmation-sub-header">
        <div className="container">
          <div className="row">
            <div className="col d-flex-center">
              <Typography component="h2" variant="body1" className="fw-b">
                {campaign?.campaign_name}
              </Typography>
            </div>
          </div>
        </div>
      </div>
      <ParticipantPageLayout className="confirm-page mx-lg-5 mx-3">
        <div className="join-campaign-confirmation mt-2">
          <Typography
            component="h1"
            variant="h4"
            className="mb-2"
            color="text.primary"
          >
            Confirmation
          </Typography>
          <Typography variant="caption" className="mb-2">
            Review your selections and make changes if required before you go to
            payment.
          </Typography>

          {!campaign?.participant_campaign ? (
            renderNoLocation()
          ) : (
            <>
              {renderLocationsList()}
              {renderTotals()}

              {!isJoinCampTnCChecked && selectedBrand?.location_fees_covered ? (
                <TermsAndConditions
                  isChecked={isTnCChecked}
                  handleTermsAndCondition={(e: any) => {
                    setIsTnCChecked(e.target.checked);
                  }}
                />
              ) : null}

              <div className="row my-5 align-items-center">
                <div className="col-lg-7 note mb-4 mb-lg-0">
                  <Typography variant="caption">
                    Once 'Confirm' is selected, you can't make changes, your
                    selections will be final for this campaign.
                  </Typography>
                </div>
                <div className="col-lg-5 d-flex align-items-center justify-content-between justify-content-lg-end">
                  <Button
                    color="primary"
                    variant="outlined"
                    component={Link}
                    to={{
                      pathname: `/participant/join-campaign/${state?.campaignId}`,
                      state: {
                        campaignId: state?.campaignId,
                        from: "participant/confirm",
                      },
                    }}
                  >
                    Make Changes
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      campaign?.enable_payment_through_portal
                        ? setOpenPayment(true)
                        : handleConfirm(0);
                    }}
                    className="ml-3 px-5"
                    disabled={!isTnCChecked}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </>
          )}

          {openPayment ? (
            <ConfirmStripeEmbedCheckoutPopup
              makePayment={openPayment}
              title="Payment"
              data={{
                email: loggedInUser?.email,
                amount: (totals as any).amountYouPay,
                fk_brand_id: selectedBrand?.id,
                system_generated_brand_id:
                  selectedBrand?.system_generated_brand_id,
                fk_brand_name: selectedBrand?.brand_name,
                ...dataToSave(1),
              }}
              onClose={() => setOpenPayment(false)}
              return_uri={`/join-campaign-return?join_campaign=true`}
              api={stripeMicroService.joinCampaignPayment}
            />
          ) : null}

          {editLocation && (
            <EditLocationModal
              location={editLocation}
              onClose={() => setEditLocation(null)}
              campaign={campaign}
              onSave={handleOnUpdateLocation}
            />
          )}
        </div>
      </ParticipantPageLayout>
      <FullScreenLoader
        isActive={isUpdatingLocation}
        message="Please wait..."
      />
    </PageLayout>
  );
};

export default JoinCampaignConfirmationPage;
